/**
 * Блок «Торговля по районам и городу» (US-30 FR-5): розничная и оптовая
 * торговля территорий с ИФО, расчётными «всего» и «долей в области» за
 * накопленный период.
 *
 * Порядок строк задаёт ответ — первой идёт сама выбранная территория,
 * далее областной центр и районы в порядке источника; экран не сортирует
 * и не переупорядочивает (FR-5.2). ИФО печатается как есть, без
 * ограничения сверху: 654,9 % (Шал акына, оптовая) и 52,3 % (Акжарский) —
 * фактические значения выпуска, а не ошибка (FR-5.4).
 *
 * Клик по строке — навигация на страницу «Торговля» этой территории
 * (FR-5.6): раздел и КАТО пишутся в query, чтобы «назад» возвращал в
 * точку до перехода.
 */
import { useTranslation } from 'react-i18next';

import {
  PeriodCaption,
  SourceNote,
  StatIfoValue,
  StatNoData,
  StatNumber,
  fmt1,
} from './statValue';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { useKato } from '../../../contexts/KatoContext';
import { cn } from '../../../lib/cn';
import type { TradeTerritoriesResponse } from '../../../types/analytics';

interface TradeTerritoriesProps {
  data: TradeTerritoriesResponse | null;
  loading: boolean;
}

export const TradeTerritories = ({ data, loading }: TradeTerritoriesProps) => {
  const { t } = useTranslation();
  const { selectedKato, setSelectedKato } = useKato();

  const rows = data?.rows ?? [];
  const unit =
    rows.find((row) => row.retail?.value?.unit != null)?.retail?.value?.unit ??
    null;

  const open = (kato: number) => {
    if (selectedKato !== null) {
      window.history.replaceState(
        window.history.state,
        '',
        `?section=trade&kato=${selectedKato}`,
      );
    }
    window.history.pushState(
      window.history.state,
      '',
      `?section=trade&kato=${kato}`,
    );
    setSelectedKato(kato);
  };

  return (
    <Card className="shadow-card" data-testid="analytics-trade-territories">
      <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-x-4 gap-y-1 space-y-0">
        <CardTitle>{t('analytics.trade.territories.title')}</CardTitle>
        <PeriodCaption
          period={data?.period ?? null}
          suffix={unit}
          testId="trade-territories-period"
        />
      </CardHeader>
      <CardContent>
        {loading ? (
          <BlockLoader />
        ) : rows.length === 0 ? (
          <StatNoData text={t('common.noData')} />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="border-surface-border hover:bg-transparent">
                  <TableHead className="h-auto py-2 align-bottom text-text-faint">
                    {t('analytics.trade.territories.columnTerritory')}
                  </TableHead>
                  <TableHead className="h-auto whitespace-nowrap py-2 text-right align-bottom text-text-faint">
                    {t('analytics.trade.territories.columnRetail')}
                  </TableHead>
                  <TableHead className="h-auto whitespace-nowrap py-2 text-right align-bottom text-text-faint">
                    {t('analytics.stats.ifoShort')}
                  </TableHead>
                  <TableHead className="h-auto whitespace-nowrap py-2 text-right align-bottom text-text-faint">
                    {t('analytics.trade.territories.columnWholesale')}
                  </TableHead>
                  <TableHead className="h-auto whitespace-nowrap py-2 text-right align-bottom text-text-faint">
                    {t('analytics.stats.ifoShort')}
                  </TableHead>
                  <TableHead className="h-auto whitespace-nowrap py-2 text-right align-bottom text-text-faint">
                    {t('analytics.trade.territories.columnTotal')}
                  </TableHead>
                  <TableHead className="h-auto whitespace-nowrap py-2 text-right align-bottom text-text-faint">
                    {t('analytics.trade.territories.columnShare')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => {
                  const kato = row.kato;
                  // Своя строка никуда не ведёт: переход — только вниз,
                  // на страницу подчинённой территории (FR-5.6).
                  const clickable = kato !== null && !row.current;
                  return (
                    <TableRow
                      key={kato ?? row.name ?? index}
                      data-testid={`trade-territory-${kato ?? row.name ?? index}`}
                      className={cn(
                        'border-surface-border',
                        row.current && 'bg-surface-muted/50 font-bold',
                        clickable
                          ? 'cursor-pointer hover:bg-brand-50/60'
                          : 'hover:bg-transparent',
                      )}
                      tabIndex={clickable ? 0 : undefined}
                      role={clickable ? 'button' : undefined}
                      onClick={clickable ? () => open(kato) : undefined}
                      onKeyDown={
                        clickable
                          ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                open(kato);
                              }
                            }
                          : undefined
                      }
                    >
                      <TableCell className="py-3 font-semibold leading-snug">
                        {row.name ?? '—'}
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <StatNumber value={row.retail?.value ?? null} />
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <StatIfoValue ifo={row.retail?.ifo ?? null} />
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <StatNumber value={row.wholesale?.value ?? null} />
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <StatIfoValue ifo={row.wholesale?.ifo ?? null} />
                      </TableCell>
                      {/* «Всего» и «доля» — расчётные величины ответа
                          (FR-5.3): экран их не считает. */}
                      <TableCell className="py-3 text-right">
                        <StatNumber value={row.total} />
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <StatNumber value={row.share} format={fmt1} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <p className="mt-3 text-xs text-text-faint">
              {t('analytics.trade.territories.rowHint')}
            </p>
            <SourceNote source={data?.source ?? null} />
          </>
        )}
      </CardContent>
    </Card>
  );
};
